import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { SortDirection } from '@ddt-web/common';
import { CellData } from '@ddt-web/data-service';
import { StatusCellRendererComponent } from '@ddt-web/fund-stats';
import { CellClassParams, ColDef, ColGroupDef, GridOptions, ValueFormatterParams } from 'ag-grid-community';
import * as get from 'lodash/get';

import { HeaderCellRendererComponent, ISharesCellRendererComponent, LightbulbCellRendererComponent, LinkCellRendererComponent, WorstOffenderBadgeCellRendererComponent } from './cell-renderers';
import { NoRowsOverlayComponent } from './no-rows-overlay/no-rows-overlay.component';

@Component({
	selector: 'ac-ag-grid-table',
	templateUrl: './ag-grid-table.component.html',
	styleUrls: ['./ag-grid-table.component.scss']
})
export class AgGridTableComponent implements OnChanges, OnInit {
	@Input() public dataArray: Array<{ [key: string]: CellData }>;
	@Input() public columnDefs: { [key: string]: { headerData: CellData; selected: boolean } };
	@Input() public rowDataIdColumn?: string = 'ticker';
	@Output() public sortMethod: EventEmitter<{ sortKey: string; sortDirection: SortDirection }> = new EventEmitter<{ sortKey: string; sortDirection: SortDirection }>();

	public gridOptions: GridOptions = {
		suppressColumnMoveAnimation: true,
		suppressColumnVirtualisation: true,
		deltaRowDataMode: true,
		components: {
			iSharesCellRenderer: ISharesCellRendererComponent,
			lightbulbCellRenderer: LightbulbCellRendererComponent,
			linkCellRenderer: LinkCellRendererComponent,
			statusCellRenderer: StatusCellRendererComponent,
			noRowsOverlayComponent: NoRowsOverlayComponent,
			worstOffenderBadgeCellRenderer: WorstOffenderBadgeCellRendererComponent
		},
		onGridReady: () => {
			this.resetGrid();
		},
		getRowClass: (params) => {
			if (params.data.lightbulb?.attribute.isChild) {
				return 'fund-idea-row';
			}
		}
	};
	private currentGroupName: string;
	private isGridReady: boolean = false;
	private readonly maxGridHeight: number = 700;
	private isUsingDefaultSort: boolean = false;
	public gridStyle: any = { height: `${this.maxGridHeight}px` };

	constructor(private elementRef: ElementRef) { }

	public ngOnInit(): void {
		this.isUsingDefaultSort = this.sortMethod.observers.length < 1;
		this.initGrid();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.gridOptions.getRowNodeId = (data) => {
			const rowId = get(data, `${this.rowDataIdColumn}.attribute.agGridRowIdentifier`) || get(data, `${this.rowDataIdColumn}`);
			return rowId;
		};

		if (this.isGridReady) {
			const isColumnDefsInputChanged: boolean =
				!!get(changes, 'columnDefs') &&
				JSON.stringify(changes.columnDefs.currentValue) !== JSON.stringify(changes.columnDefs.previousValue) &&
				!changes.columnDefs.isFirstChange();
			const isDataArrayInputChanged: boolean =
				!!get(changes, 'dataArray') &&
				JSON.stringify(changes.dataArray.currentValue) !== JSON.stringify(changes.dataArray.previousValue) &&
				!changes.dataArray.isFirstChange();

			if (isColumnDefsInputChanged) {
				this.setColumnVisibility();
				this.gridOptions.api.setColumnDefs(this.getColumnDefsForGrid());
				this.gridOptions.api.setHeaderHeight(this.getHeaderHeight());
			}

			if (isDataArrayInputChanged) {
				if (this.dataArray instanceof Array) {
					this.updateRowData();
				}
			}
			this.updateAutoHeight();
		}
	}

	/**
	 * updateAutoHeight: updates the domLayout property based on the height of the ag-grid vs the maximum height allowed.
	 */
	private updateAutoHeight(): void {
		const agWrapperElement = this.elementRef.nativeElement.querySelector('.ag-full-width-container') as HTMLElement;

		// If the height of the generated ag-grid is greater than our max height we'll suppress the auto height attribute of the ag-grid.
		if (agWrapperElement && agWrapperElement.offsetHeight > this.maxGridHeight) {
			this.gridStyle = { height: `${this.maxGridHeight}px` };
			this.gridOptions.api.setDomLayout('normal');
		} else {
			this.gridStyle = {};
			this.gridOptions.api.setDomLayout('autoHeight');
		}
	}

	private setColumnVisibility(): void {
		const columnToShow = [];
		const columnToHide = [];
		Object.values(this.columnDefs).forEach(column => {
			if (column.selected || column.headerData.attribute['pinned']) {
				columnToShow.push(column.headerData.config.id);
			} else {
				columnToHide.push(column.headerData.config.id);
			}
		});
		if (columnToShow.length) {
			this.gridOptions.columnApi.setColumnsVisible(columnToShow, true);
		}
		if (columnToHide.length) {
			this.gridOptions.columnApi.setColumnsVisible(columnToHide, false);
		}
	}

	private initGrid(): void {
		if (this.columnDefs) {
			this.gridOptions.columnDefs = this.getColumnDefsForGrid();
		}
	}

	private getColumnDefsForGrid(): (ColDef | ColGroupDef)[] {
		this.currentGroupName = '';
		return Object.values(this.columnDefs).map(column => {
			const cellClassList: string[] = this.getCellClass(column);

			return Object.assign(
				{
					headerName: column.headerData.formattedValue,
					field: column.headerData.config.id,
					colId: column.headerData.config.id,
					autoHeight: true,
					cellClass: cellClassList,
					width: column.headerData.attribute['columnWidth'] || 110,
					cellStyle: this.cellStyling,
					headerComponentFramework: HeaderCellRendererComponent,
					valueFormatter: (params: ValueFormatterParams) => params.value?.formattedValue ?? '',
					headerComponentParams: {
						infoBubble: column.headerData.attribute['infoBubble'],
						infoBubbleKey: column.headerData.config?.infoBubble?.key,
						isHeaderHidden: column.headerData.attribute['isHeaderHidden'],
						isInfoBubbleModal: column.headerData.attribute['isInfoBubbleModal'],
						classList: [...cellClassList, 'text-bold'],
						styleList: { width: column.headerData.attribute['columnWidth'] || 110 + 'px' },
						sortMethod: this.isUsingDefaultSort
							? null
							: (sortParams: { sortKey: string; direction: string }) => {
								this.triggerManualSort(sortParams);
							},
						defaultSort: column.headerData.attribute['defaultSort']
					},
					suppressMovable: true,
					sortable: !column.headerData.attribute['isInfoBubbleModal'],
					hide: !(column.selected || column.headerData.attribute['pinned'])
				},
				column.headerData.attribute['pinned'] ? { pinned: 'left' } : {},
				column.headerData.attribute['cellRenderer'] ? this.getCellRendererParams(column.headerData.attribute['cellRenderer']) : {},
				column.headerData.attribute['defaultSort'] && this.isUsingDefaultSort ? { sort: 'asc' } : {},
				this.isUsingDefaultSort ? {} : { comparator: () => 0 }
			);
		});
	}

	private getCellRendererParams(cellRendererType: string): any {
		switch (cellRendererType) {
			case 'link':
				return { cellRenderer: 'linkCellRenderer' };
			case 'ishares':
				return { cellRenderer: 'iSharesCellRenderer' };
			case 'lightbulb':
				return { cellRenderer: 'lightbulbCellRenderer' };
			case 'status':
				return { cellRenderer: 'statusCellRenderer' };
			case 'badge':
				return { cellRenderer: 'worstOffenderBadgeCellRenderer' };
			default:
				console.error('unknown cell renderer name');
				return {};
		}
	}

	public resetGrid(): void {
		this.isGridReady = true;
		if (this.gridOptions.api) {
			if (this.dataArray instanceof Array) {
				this.updateRowData();
			}
			setTimeout(() => {
				if (this.gridOptions.api) {
					this.gridOptions.api.setHeaderHeight(this.getHeaderHeight());
				}
			}, 1000);
			setTimeout(() => {
				if (this.gridOptions.api) {
					this.gridOptions.api.resetRowHeights();
					setTimeout(() => {
						this.updateAutoHeight();
					}, 0);
				}
			}, 2000);
		}
	}

	private getHeaderHeight(): number {
		let headerCells = this.elementRef.nativeElement.querySelectorAll('.header-container');
		// stupid IE
		headerCells = Array.from(headerCells);
		let minHeight = 40;
		headerCells.forEach(cell => {
			minHeight = Math.max(minHeight, cell.scrollHeight);
		});
		return minHeight + 20;
	}

	public getCellClass(column: { headerData: CellData; selected: boolean }, params?: CellClassParams): string[] {
		const fundIdeaHighlightColumnFields: string[] = ['lightbulb', 'status', 'ticker', 'description'];

		const classList = ['ac-ag-grid-cell-wrap-text', column.headerData.attribute['columnAlignment'], 'text-14'];
		const isColumnSelected = column.selected && !column.headerData.attribute['pinned'];
		const isColumnAStartOfNewGroup =
			this.currentGroupName && column.headerData.attribute['columnGroup'] && this.currentGroupName !== column.headerData.attribute['columnGroup'];
		if (isColumnSelected && isColumnAStartOfNewGroup) {
			classList.push('ac-ag-grid-cell-first-in-group');
			this.currentGroupName = column.headerData.attribute['columnGroup'];
		} else if (!this.currentGroupName && isColumnSelected) {
			this.currentGroupName = column.headerData.attribute['columnGroup'];
		}

		if (params?.data['lightbulb']?.attribute?.isChild && fundIdeaHighlightColumnFields.includes(params.colDef.field)) {
			classList.push('fund-idea-background-color');
		}

		return classList;
	}

	public cellStyling(params: any): any {
		const styling = Object.assign(
			{},
			params.value.attribute['backgroundColor']
				? {
					'background-color': params.value.attribute['backgroundColor'],
					'border-color': 'white',
					'border-style': 'solid',
					'border-width': '0px 0px 1px 1px'
				}
				: {},

			params.value.attribute['isParent'] ? { 'border-top': '2px solid black' } : {},
			params.value.attribute['isChild'] ? { 'border-bottom': '2px solid black' } : {}
		);
		return styling;
	}

	public triggerManualSort(sortParams: { sortKey: string; direction: string }): void {
		let sortDirection = SortDirection.UNSORTED;
		if (sortParams.direction === 'asc') {
			sortDirection = SortDirection.ASC;
		} else if (sortParams.direction === 'desc') {
			sortDirection = SortDirection.DESC;
		}
		this.sortMethod.emit({ sortKey: sortParams.sortKey, sortDirection: sortDirection });
	}

	private updateRowData(): void {
		this.gridOptions.api.setRowData(this.dataArray);
	}
}
